import type { AccountSource } from 'owa-account-source-list-store';
import { isLocalDataBackedAccount } from './isLocalDataBackedAccount';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { PolicyStateTypeEnum } from 'owa-account-source-list-types';
import { getAccountAuthStateForMailboxInfo, StateTypeEnum } from 'owa-account-authstate-store';
import { accountRankTypeChecker } from 'owa-account-source-list-store';

/**
 * Gets the formatted account description in the format of "Account Description (SMTP Address)".
 * @param {AccountSource} accountSource The account source object.
 * @returns {string | undefined} The formatted account description or undefined if the account description should not be shown.
 */
export function getFormattedAccountDescription(accountSource: AccountSource): string | undefined {
    const smtpAddress = accountSource.mailboxInfo.mailboxSmtpAddress;
    if (
        !shouldShowAccountDescription(accountSource) ||
        !accountSource.accountDescription ||
        accountSource.accountDescription.toLowerCase() === smtpAddress.toLowerCase()
    ) {
        return undefined;
    }
    return `${accountSource.accountDescription} (${smtpAddress})`;
}
/**
 * Determines whether the account description should be displayed.
 * @param accountSource The account source object.
 * @returns `true` if the account description should be shown; otherwise, `false`.
 */
export const shouldShowAccountDescription = (accountSource: AccountSource): boolean => {
    if (
        !isGlobalFeatureEnabled('acct-accountDescription') ||
        isLocalDataBackedAccount(accountSource)
    ) {
        return false;
    }

    const hasPolicyError = accountRankTypeChecker.isCoprincipal(accountSource)
        ? accountSource.policyState === PolicyStateTypeEnum.PolicyError
        : false;

    const hasAuthError =
        getAccountAuthStateForMailboxInfo(accountSource.mailboxInfo).stateType ===
        StateTypeEnum.AuthError;

    if (hasPolicyError || hasAuthError) {
        return false;
    }

    return true;
};
